import * as Mutations from './mutations';
import { InvoicesService } from '@/common/services/pages/invoices.service';
import { PaginationParams } from '@/common/utils/pagination-params';
import { clearAllSelected } from '@/store/modules/page/reset';
import { CURRENCY_LIST } from '@/constants/currencies';

export const InvoicesModule = {
    state: {
        invoices: null,
        invoice: null,
        invoiceId: null,
        couponCode: null,
        paymentInfo: null
    },

    mutations: {
        [Mutations.SET_INVOICES](state, invoices) {
            state.invoices = invoices;
        },

        [Mutations.SET_INVOICE] (state, invoice) {
            state.invoice = invoice;
        },

        [Mutations.SET_INVOICE_ID] (state, invoiceId) {
            state.invoiceId = invoiceId;
        },

        [Mutations.SET_COUPON_CODE] (state, couponCode) {
            state.couponCode = couponCode;
        },

        [Mutations.SET_PAYMENT_INFO] (state, paymentInfo) {
            state.paymentInfo = paymentInfo;
        },

        [Mutations.RESET_PAYMENT_INFO] (state) {
            state.paymentInfo = null;
        }
    },

    actions: {
        async getInvoices(context) {
            const { data: { content, pageable, totalPages, totalElements, numberOfElements } } =
                await InvoicesService.getInvoices(context.getters.schoolId,
                    PaginationParams.createParams({
                        size: context.getters.pageSize,
                        page: context.getters.pageNumber,
                        sort: context.getters.sortBy,
                        direction: context.getters.direction,
                        query: context.getters.searchQuery,
                        filters: {
                            ...context.getters.filters
                        }
                    }));

            context.commit(Mutations.SET_INVOICES, content);
            context.dispatch('configPagination', {
                ...pageable,
                numberOfElements,
                totalElements,
                totalPages,
                callback: () => {
                    return context.dispatch('getInvoices');
                }
            });
        },

        async previewInvoice(context, invoiceId) {
            context.commit(Mutations.SET_INVOICE_ID, invoiceId);
            await context.dispatch('getInvoice');
            context.dispatch('openModal', {
                modalName: 'InvoicePreviewModal',
                callback: () => context.dispatch('unstageInvoice'),
                options: {
                    width: '680px',
                    info: context.getters.invoice
                }
            });
        },

        async getInvoice(context) {
            let { data } = await InvoicesService.getInvoice(context.getters.schoolId, context.getters.invoiceId);

            data = {
                ...data,
                download: true,
                currency: data && CURRENCY_LIST.find(currency => currency.short === _.upperCase(data.currency)).long
            };

            context.commit(Mutations.SET_INVOICE, data);
        },

        async downloadInvoicePdf(context, id) {
            const { data } = await InvoicesService.downloadInvoicePdf(context.getters.schoolId, id);

            window.open(data, '_blank');
        },

        async sendInvoiceReceipt(context) {
            await InvoicesService.sendInvoiceReceipt(context.getters.schoolId, context.getters.invoiceId);

            context.dispatch('createSuccessNotification', 'You have successfully send invoice receipt');
        },

        unstageInvoice(context) {
            context.commit(Mutations.SET_INVOICE_ID, null);
            context.commit(Mutations.SET_INVOICE, null);
        },

        async saveNewMemo(context) {
            await InvoicesService.saveNewMemo(context.getters.schoolId, context.getters.textareaId, context.getters.textareaNote);

            context.dispatch('createSuccessNotification', 'You have successfully edit memo');
        },

        async addInvoice(context, payload) {
            await InvoicesService.addInvoice(context.getters.schoolId, payload);

            await context.dispatch('getInvoices');
        },

        async getInvoicePaymentInfo(context, payload) {
            try {
                const { activityType, listType } = payload;
                const { data } = await InvoicesService[`get${activityType}${listType}InvoicePaymentInfo`](context.getters.schoolId, payload);

                context.commit(Mutations.SET_PAYMENT_INFO, data);
            }
            catch (e) {
                const { data } = e.response;
                data && data.showMessageToUser && context.commit(Mutations.SET_COUPON_CODE, null);
                throw e;
            }
        },

        async makeParentPay(context, payload) {
            const { id, listType, form } = payload;

            const formCoupon = {
                ...form,
                couponCode: context.getters.couponCode
            };
            await InvoicesService.makeParentPay(context.getters.schoolId, id, listType, formCoupon);

            context.commit(Mutations.SET_COUPON_CODE, null);
        },

        async removeAddonFromPayment(context, payload)  {
            const { addOnId, id, activityType, listType, activityId, childId, couponCode, form } = payload;

            await InvoicesService.removeAddonFromActivity(context.getters.schoolId, id, addOnId, activityType, listType);

            context.dispatch('getInvoicePaymentInfo', { activityId, activityType, listType, childId, couponCode, form });
        },

        async deleteInvoice(context, invoiceId) {
            await InvoicesService.deleteInvoice(context.getters.schoolId, invoiceId);
            clearAllSelected();
            await context.dispatch('getInvoices');
        },

        async deleteMultipleInvoices(context) {
            await InvoicesService.deleteMultipleInvoices(context.getters.schoolId, context.getters.selected);
            await context.dispatch('getInvoices');
            context.dispatch('resetSelectedItems');
        },

        async invoicesResend(context, payload) {
            const { data, number } = payload;
            const { schoolId } = context.getters;
            _.isArray(data) && data.length
                ? await InvoicesService.invoiceBulkResend(schoolId, data)
                : await InvoicesService.invoiceResend(schoolId, data);
            _.isArray(data) && context.dispatch('resetSelectedItems');
            context.dispatch('createSuccessNotification', _.isArray(data) ? 'You have successfully resend invoices' : `You have successfully resend invoice ${number}`);
        },

        resetPaymentInfo(context) {
            context.commit(Mutations.RESET_PAYMENT_INFO);
        }
    },

    getters: {
        invoices: state => state.invoices,

        invoice: state => state.invoice,

        invoiceId: state => state.invoiceId,

        couponCode: state => state.couponCode,

        paymentFullInfo: state => state.paymentInfo,

        paymentInfo: state => state.paymentInfo && (state.paymentInfo.roster ? state.paymentInfo.roster : state.paymentInfo.dropin)
    }
};


// WEBPACK FOOTER //
// ./src/store/modules/invoices/invoices.module.js